import {
  FC,
  useState,
  useCallback,
  PropsWithChildren,
  memo,
  HTMLAttributes,
} from "react";
import { StarIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/24/solid";
import { League as LeagueType } from "./types/football_table";
import { ArrowIcon } from "./icons";
import Flag from "./flag";
import classNames from "classnames";
import { THEME, THEME_LIVE } from "./config";
import { useTranslation } from "react-i18next";

// import Refresh from "../Refresh";

export type LeagueProps = {
  data?: LeagueType;
  isLive?: boolean;
  hasMl?: boolean;
  // isLoading?: boolean;
  isFlagVisible?: boolean;
  // onClickRefresh?: () => void;
  onClickPinFavorite?: (
    payload: { id: string; name: string },
    type: "LEAGUE" | "TEAM",
  ) => void;
};

const League: FC<
  PropsWithChildren<LeagueProps> & HTMLAttributes<HTMLDivElement>
> = ({
  data,
  // onClickRefresh,
  isLive = false,
  isFlagVisible = false,
  hasMl = false,
  // isLoading,
  children,
  className,
  onClickPinFavorite,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const onClick = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);
  const theme = isLive ? THEME_LIVE : THEME;
  const { t, i18n } = useTranslation();
  const leagueName =
    data?.league?.[i18n.language as keyof LeagueType["league"]] ||
    data?.league?.en;

  return (
    <table
      className={classNames(
        `text-xs relative leading-6 w-full`,
        theme.borderColor,
        className,
      )}>
      <colgroup>
        <col
          className={`border-x ${theme.borderColor}`}
          style={{ minWidth: "89px", maxWidth: "89px", width: "89px" }}
        />
        <col
          className={`border-x ${theme.borderColor}`}
          style={{ minWidth: "160px", maxWidth: "360px", width: "360px" }}
        />
        <col
          className={`border-x ${theme.borderColor}`}
          style={{ minWidth: "100px", maxWidth: "100px", width: "100px" }}
        />
        <col
          className={`border-x ${theme.borderColor}`}
          style={{ minWidth: "120px", maxWidth: "120px", width: "120px" }}
        />
        <col
          className={`border-x ${theme.borderColor}`}
          style={{ minWidth: "68px", maxWidth: "68px", width: "68px" }}
        />
        <col
          className={`border-x ${theme.borderColor}`}
          style={{ minWidth: "80px", maxWidth: "80px", width: "80px" }}
        />

        <col
          className={`border-x ${theme.borderColor}`}
          style={{ minWidth: "100px", maxWidth: "100px", width: "100px" }}
        />
        <col
          className={`border-x ${theme.borderColor}`}
          style={{ minWidth: "120px", maxWidth: "120px", width: "120px" }}
        />
        <col
          className={`border-x ${theme.borderColor}`}
          style={{ minWidth: "68px", maxWidth: "68px", width: "68px" }}
        />
        <col
          className={`border-x ${theme.borderColor}`}
          style={{ minWidth: "50px", maxWidth: "50px", width: "50px" }}
        />
      </colgroup>
      <thead className="sticky top-0 text-white">
        {!!data && (
          <tr className={theme.primaryA}>
            <th colSpan={12} className={`border ${theme.borderColor}`}>
              <div className="flex py-2 px-4">
                <div className="flex-1 text-left gap-x-2 flex items-center">
                  <button
                    className="align-text-bottom mr-1"
                    onClick={() => {
                      onClickPinFavorite?.(
                        {
                          id: `${data.id}`,
                          name: data.league.en,
                        },
                        "LEAGUE",
                      );
                    }}>
                    {Boolean(data.is_favorite) ?
                      <StarSolidIcon className="w-5 h-5 text-yellow-400" />
                    : <StarIcon className="w-5 h-5 text-yellow-400" />}
                  </button>

                  {isFlagVisible && (
                    <Flag
                      src={data?.flag_img}
                      flagId={data.flag_id}
                      type="tournaments"
                    />
                  )}
                  <button onClick={onClick} className="cursor-pointer">
                    {leagueName}
                  </button>
                  <div
                    className="bg-white text-black rounded text-center"
                    style={{ minWidth: 20 }}>
                    {data.matches_no}
                  </div>
                </div>
                <button
                  onClick={onClick}
                  className={classNames("cursor-pointer sticky right-3", {
                    "-rotate-90": !isOpen,
                  })}>
                  <ArrowIcon />
                </button>
                {/* <Refresh
                  noLabel
                  isLoading={isLoading}
                  onClick={onClickRefresh}
                  initialCounter={0}>
                  <RefreshIcon className={isLoading ? "animate-spin" : ""} />
                </Refresh> */}
              </div>
            </th>
          </tr>
        )}

        <tr
          className={classNames(theme.borderColor, theme.primaryB, `border`, {
            hidden: !isOpen,
          })}>
          <th className={`border ${theme.borderColor}`} rowSpan={2}>
            {t("Time")}
          </th>
          <th className={`border ${theme.borderColor}`} rowSpan={2}>
            {t("Event")}
          </th>
          <th className={`border ${theme.borderColor}`} colSpan={4}>
            {t("Full Time")}
          </th>
          <th className={`border ${theme.borderColor}`} colSpan={3}>
            {t("First Half")}
          </th>
          <th className={`border ${theme.borderColor}`} rowSpan={2}>
            {t("More")}
          </th>
        </tr>
        <tr
          className={classNames(theme.borderColor, theme.primaryB, `border`, {
            hidden: !isOpen,
          })}>
          <th className={`border ${theme.borderColor}`}>HDP</th>
          <th className={`border ${theme.borderColor}`}>Over/Under</th>
          <th className={`border ${theme.borderColor}`}>
            {hasMl ? "ML" : "12X"}
          </th>
          <th className={`border ${theme.borderColor}`}>Odd/Even</th>
          <th className={`border ${theme.borderColor}`}>HDP</th>
          <th className={`border ${theme.borderColor}`}>Over/Under</th>
          <th className={`border ${theme.borderColor}`}>
            {hasMl ? "ML" : "12X"}
          </th>
        </tr>
      </thead>
      <tbody
        className={classNames("border-b-0 text-black bg-white", {
          hidden: !isOpen,
        })}>
        {children}
      </tbody>
    </table>
  );
};

export default memo(League);
